import { ShieldCheck } from "lucide-react";
import { insuranceStatusFor, SELF_PAY_ID } from "@/lib/insurance-catalog.functions";
import { cn } from "@/lib/utils";

/**
 * "In-network · Blue Cross Blue Shield of Texas" — the single network-status
 * line, shared by the search card and the provider profile so the two can never
 * disagree about the same doctor.
 *
 * Deliberately one plain line: a shield, the status, a middot, the carrier.
 * No pill background or border — at 20 cards a boxed badge competed with the
 * name for attention, and this is meant to be read at a glance, not looked at.
 *
 * Renders nothing when no insurance is selected; there is no status to state.
 */
export function InsuranceNetworkBadge({
  providerInsurance,
  token,
  label,
  className,
}: {
  providerInsurance: string[] | null | undefined;
  /** Selected carrier or plan token, e.g. "aetna" or "aetna:choice-pos-ii". */
  token: string | null;
  /** Carrier or plan name to show after the middot. */
  label: string;
  className?: string;
}) {
  const status = insuranceStatusFor(providerInsurance, token);
  if (!status || !label) return null;

  // Self-pay is not a network, so the network wording is meaningless for it
  // ("Out-of-network · Paying for myself" says nothing). It also needs no
  // trailing label — the status sentence already names it.
  const selfPay = token === SELF_PAY_ID;

  // Only in-network gets colour. Out-of-network and unknown are information,
  // not warnings — an empty insurance[] usually means nobody has asked the
  // practice yet, so red would be an accusation the data cannot support.
  const text = selfPay
    ? status === "in"
      ? "Accepts self-pay patients"
      : status === "out"
        ? "Self-pay not listed"
        : "Contact office about self-pay"
    : status === "in"
      ? "In-network"
      : status === "out"
        ? "Out-of-network"
        : "Contact office to confirm";

  if (selfPay) {
    return (
      <p
        className={cn(
          "flex items-center gap-1.5 text-[13px]",
          status === "in" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground",
          className,
        )}
      >
        {status === "in" && <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />}
        <span className="truncate font-medium">{text}</span>
      </p>
    );
  }

  return (
    <p
      className={cn(
        "flex items-center gap-1.5 text-[13px]",
        status === "in" ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground",
        className,
      )}
    >
      {status === "in" && <ShieldCheck className="h-4 w-4 shrink-0" aria-hidden />}
      <span className="truncate">
        <span className="font-medium">{text}</span>
        <span aria-hidden> · </span>
        <span className="sr-only"> with </span>
        {label}
      </span>
    </p>
  );
}
